import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { CastingVideoImporter } from './CastingVideoImporter';
import LibraryPicker, { type StagedLibraryFile } from './library/LibraryPicker';
import { LoginModal } from './LoginModal';
import { getAuthHeaders } from '@/hooks/useAuth';
import {
  Upload,
  Import,
  Mic,
  Copy,
  CheckCircle,
  AlertCircle,
  Loader2,
  Trash2,
  FolderOpen,
  ChevronDown,
  X,
  Play,
  Search,
  CheckSquare,
  Square,
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { resolveVideoUrl } from '@/lib/utils';

interface ProjectFile {
  id: string;
  name: string;
  fileType?: 'video' | 'audio';
  thumbnailUrl?: string;
  duration: number;
  timestamp?: string;
  projectName?: string;
  sessionName?: string;
  groupName?: string;
  takeName?: string;
  talent?: string[];
  s3Url?: string;
  s3Key?: string;
  bucket?: string;
  imported?: boolean;
  transcriptionStatus?: 'READY_FOR_TRANSCRIPTION' | 'TRANSCRIBING' | 'DONE' | 'FAILED';
  transcriptionJobId?: string;
  transcript?: string;
  transcriptJson?: any;
}

export const VideoTranscriber = () => {
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [currentProject, setCurrentProject] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [playingVideo, setPlayingVideo] = useState<ProjectFile | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSearchResult, setCurrentSearchResult] = useState(0);
  const [searchMatches, setSearchMatches] = useState<Array<{fileId: string, matchCount: number}>>([]);
  const [expandedTranscripts, setExpandedTranscripts] = useState<string[]>([]);
  const { toast } = useToast();
  
  // Use refs to always access latest values in callbacks
  const filesRef = useRef<ProjectFile[]>([]);
  const currentProjectRef = useRef<string | null>(null);
  
  // Keep refs in sync with state
  filesRef.current = files;
  currentProjectRef.current = currentProject;

  // Load projects on mount
  useEffect(() => {
    const savedProjects = localStorage.getItem('transcriberProjects');
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    }
    
    const savedCurrentProject = localStorage.getItem('currentTranscriberProject');
    if (savedCurrentProject) {
      setCurrentProject(savedCurrentProject);
      loadProjectFiles(savedCurrentProject);
    }
  }, []);

  // Set up polling for transcription status
  useEffect(() => {
    console.log('[Polling] Setting up interval');
    const interval = setInterval(() => {
      const transcribingFiles = filesRef.current.filter(f => f.transcriptionStatus === 'TRANSCRIBING');
      if (transcribingFiles.length > 0) {
        console.log(`[Polling] Checking status for ${transcribingFiles.length} transcribing files`);
        checkTranscriptionStatus();
      }
    }, 5000); // Check every 5 seconds

    return () => {
      console.log('[Polling] Clearing interval');
      clearInterval(interval);
    };
  }, []); // Only set up once

  const loadProjectFiles = (projectName: string) => {
    const projectData = localStorage.getItem(`transcriberProject_${projectName}`);
    if (projectData) {
      const parsed = JSON.parse(projectData);
      setFiles(parsed.files || []);
    }
  };

  const saveProjectData = (projectName: string, projectFiles: ProjectFile[]) => {
    const projectData = {
      files: projectFiles,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(`transcriberProject_${projectName}`, JSON.stringify(projectData));
  };

  const checkTranscriptionStatus = async () => {
    const transcribingFiles = filesRef.current.filter(f => f.transcriptionStatus === 'TRANSCRIBING');
    console.log(`[Polling] Found ${transcribingFiles.length} transcribing files in filesRef`);
    
    for (const file of transcribingFiles) {
      if (file.transcriptionJobId) {
        try {
          console.log(`[Polling] Checking status for job ${file.transcriptionJobId}`);
          const response = await fetch(`/api/transcribe/status/${file.transcriptionJobId}`);
          if (response.ok) {
            const data = await response.json();
            console.log(`[Polling] Job ${file.transcriptionJobId} status: ${data.status}`);
            
            if (data.status === 'COMPLETED') {
              // Update file with transcript
              const updatedFiles = filesRef.current.map(f => {
                if (f.id === file.id) {
                  return {
                    ...f,
                    transcriptionStatus: 'DONE' as const,
                    transcript: data.transcript,
                    transcriptJson: data.transcriptJson
                  };
                }
                return f;
              });
              setFiles(updatedFiles);
              if (currentProjectRef.current) {
                saveProjectData(currentProjectRef.current, updatedFiles);
              }
              
              toast({
                title: 'Transcription Complete',
                description: `${file.name} has been transcribed`,
              });
            } else if (data.status === 'FAILED') {
              const updatedFiles = filesRef.current.map(f => {
                if (f.id === file.id) {
                  return {
                    ...f,
                    transcriptionStatus: 'FAILED' as const
                  };
                }
                return f;
              });
              setFiles(updatedFiles);
              if (currentProjectRef.current) {
                saveProjectData(currentProjectRef.current, updatedFiles);
              }
              
              toast({
                title: 'Transcription Failed',
                description: `Failed to transcribe ${file.name}`,
                variant: 'destructive'
              });
            }
          }
        } catch (error) {
          console.error('Error checking transcription status:', error);
        }
      }
    }
  };

  const handleTranscribe = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: 'No files selected',
        description: 'Please select at least one video to transcribe',
        variant: 'destructive'
      });
      return;
    }

    setIsTranscribing(true);

    // Get the actual file objects for the selected IDs
    const selectedFileObjects = files.filter(f => selectedFiles.includes(f.id));

    // Accumulate all file updates to avoid state closure bug
    // (each iteration needs to build on previous updates, not overwrite them)
    let accumulatedFiles = [...files];

    for (const file of selectedFileObjects) {
      try {
        const response = await fetch('/api/transcribe/start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            videoUrl: resolveVideoUrl(file.s3Url, file.s3Key) || file.s3Url,
            videoId: file.id,
            projectId: currentProject,
            fileName: file.name,
            s3Key: file.s3Key,
            bucket: file.bucket
          })
        });

        if (response.ok) {
          const data = await response.json();

          // Update accumulated files (not stale `files` state)
          accumulatedFiles = accumulatedFiles.map(f => {
            if (f.id === file.id) {
              return {
                ...f,
                transcriptionStatus: 'TRANSCRIBING' as const,
                transcriptionJobId: data.jobId
              };
            }
            return f;
          });

          toast({
            title: 'Transcription Started',
            description: `Transcribing ${file.name}...`,
          });
        } else {
          throw new Error('Failed to start transcription');
        }
      } catch (error) {
        console.error('Error starting transcription:', error);
        toast({
          title: 'Error',
          description: `Failed to start transcription for ${file.name}`,
          variant: 'destructive'
        });
      }
    }

    // Apply all accumulated updates at once
    setFiles(accumulatedFiles);
    if (currentProject) {
      saveProjectData(currentProject, accumulatedFiles);
    }

    setIsTranscribing(false);
    setSelectedFiles([]);
  };

  const copyTranscript = (transcript: string) => {
    navigator.clipboard.writeText(transcript);
    toast({
      title: 'Copied to clipboard',
      description: 'Transcript has been copied to your clipboard',
    });
  };

  const handleVideosImported = (videos: any[]) => {
    // Extract project name from first video
    const projectName = videos[0]?.castingData?.projectName || videos[0]?.projectName || 'Untitled Project';
    const sessionName = videos[0]?.castingData?.sessionName || videos[0]?.sessionName || 'Session';
    const fullProjectName = `${projectName} - ${sessionName}`;
    
    // Create/update project
    if (!projects.includes(fullProjectName)) {
      const updatedProjects = [...projects, fullProjectName];
      setProjects(updatedProjects);
      localStorage.setItem('transcriberProjects', JSON.stringify(updatedProjects));
    }
    
    setCurrentProject(fullProjectName);
    localStorage.setItem('currentTranscriberProject', fullProjectName);
    
    // Process imported videos
    const newFiles: ProjectFile[] = videos.map(video => {
      // Extract talent names - they might be in different places
      let talentArray: string[] = [];
      if (video.groupTalents && Array.isArray(video.groupTalents)) {
        // From CastingVideoImporter - groupTalents is an array of objects
        talentArray = video.groupTalents.map((t: any) => 
          typeof t === 'string' ? t : (t.name || t.talentName || '')
        ).filter(Boolean);
      } else if (video.talent && Array.isArray(video.talent)) {
        talentArray = video.talent;
      } else if (video.castingData?.talentName) {
        talentArray = [video.castingData.talentName];
      } else if (video.talentName) {
        talentArray = [video.talentName];
      }

      return {
        id: video.s3Key || Math.random().toString(36).substr(2, 9),
        name: video.name || video.videoName || 'Untitled Video',
        fileType: 'video' as const,
        thumbnailUrl: video.thumbnailUrl || video.castingData?.thumbnailUrl,
        duration: video.duration || video.castingData?.duration || 0,
        projectName: video.castingData?.projectName || video.projectName,
        sessionName: video.castingData?.sessionName || video.sessionName,
        groupName: video.groupName || video.castingData?.groupName,
        takeName: video.takeName || video.castingData?.takeName,
        talent: talentArray,
        s3Url: video.s3Url || video.url || video.videoUrl,
        s3Key: video.s3Key,
        imported: true,
        transcriptionStatus: 'READY_FOR_TRANSCRIPTION' as const
      };
    });
    
    setFiles(newFiles);
    saveProjectData(fullProjectName, newFiles);
    
    toast({
      title: 'Videos Imported',
      description: `${videos.length} video${videos.length > 1 ? 's' : ''} imported to ${fullProjectName}`,
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const videoFiles = acceptedFiles.filter(file =>
      file.type.startsWith('video/') || file.type.startsWith('audio/')
    );

    if (videoFiles.length === 0) {
      toast({
        title: 'No valid files',
        description: 'Please upload video or audio files',
        variant: 'destructive'
      });
      return;
    }

    // Ensure a project exists
    const projectName = currentProject || 'Uploads';
    if (!currentProject) {
      setProjects(prev => prev.includes('Uploads') ? prev : [...prev, 'Uploads']);
      setCurrentProject('Uploads');
      localStorage.setItem('currentTranscriberProject', 'Uploads');
    }

    toast({ title: `Uploading ${videoFiles.length} file${videoFiles.length > 1 ? 's' : ''}...` });

    for (const file of videoFiles) {
      try {
        // Get presigned upload URL
        const uploadRes = await fetch('/get-upload-url', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            ephemeral: true
          })
        });
        if (!uploadRes.ok) throw new Error('Failed to get upload URL');
        const { uploadUrl, key, downloadUrl, bucket } = await uploadRes.json();

        // Upload to S3
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.addEventListener('load', () => xhr.status === 200 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
          xhr.addEventListener('error', () => reject(new Error('Upload failed')));
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', file.type);
          xhr.send(file);
        });

        // Add file to project
        const newFile: ProjectFile = {
          id: key || Math.random().toString(36).substr(2, 9),
          name: file.name,
          fileType: file.type.startsWith('audio/') ? 'audio' : 'video',
          duration: 0,
          s3Url: downloadUrl,
          s3Key: key,
          bucket,
          imported: false,
          transcriptionStatus: 'READY_FOR_TRANSCRIPTION'
        };

        setFiles(prev => {
          const updated = [...prev, newFile];
          saveProjectData(projectName, updated);
          return updated;
        });
      } catch (err: any) {
        toast({
          title: `Failed to upload ${file.name}`,
          description: err.message,
          variant: 'destructive'
        });
      }
    }

    toast({ title: 'Upload complete', description: `${videoFiles.length} file${videoFiles.length > 1 ? 's' : ''} ready for transcription` });
  }, [toast, currentProject]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
      'audio/*': ['.mp3', '.wav', '.m4a', '.aac']
    }
  });

  // "Choose from your library" — the staged file lands in the same
  // rotator-bucket uploads/ prefix a fresh upload uses.
  const handleLibraryPick = (f: StagedLibraryFile) => {
    // Ensure a project exists (mirrors onDrop)
    const projectName = currentProject || 'Uploads';
    if (!currentProject) {
      setProjects(prev => prev.includes('Uploads') ? prev : [...prev, 'Uploads']);
      setCurrentProject('Uploads');
      localStorage.setItem('currentTranscriberProject', 'Uploads');
    }

    const newFile: ProjectFile = {
      id: f.key,
      name: f.name,
      fileType: 'video',
      duration: 0,
      s3Url: f.downloadUrl,
      s3Key: f.key,
      bucket: 'hey-joe-video-rotator',
      imported: false,
      transcriptionStatus: 'READY_FOR_TRANSCRIPTION'
    };

    setFiles(prev => {
      const updated = [...prev, newFile];
      saveProjectData(projectName, updated);
      return updated;
    });

    toast({ title: 'Added from library', description: `${f.name} is ready for transcription` });
  };

  const handleProjectChange = (projectName: string) => {
    setCurrentProject(projectName);
    localStorage.setItem('currentTranscriberProject', projectName);
    loadProjectFiles(projectName);
  };

  const handleDeleteProject = (projectName: string) => {
    const updatedProjects = projects.filter(p => p !== projectName);
    setProjects(updatedProjects);
    localStorage.setItem('transcriberProjects', JSON.stringify(updatedProjects));
    localStorage.removeItem(`transcriberProject_${projectName}`);
    
    if (currentProject === projectName) {
      setCurrentProject(null);
      localStorage.removeItem('currentTranscriberProject');
      setFiles([]);
    }
    
    toast({
      title: 'Project Deleted',
      description: `${projectName} has been removed`,
    });
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setShowImporter(true);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to highlight search terms in text
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <mark key={index} className="bg-yellow-300 text-black px-0.5 rounded">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  // Format transcript with speaker labels and paragraphs
  const formatTranscriptDisplay = (transcript: string, searchTerm: string) => {
    if (!transcript) return null;
    
    // Split by lines to preserve speaker formatting from backend
    const lines = transcript.split('\n');
    
    return lines.map((line, index) => {
      if (line.trim() === '') {
        // Empty line for paragraph break
        return <div key={index} className="h-2" />;
      }
      
      // Check if line starts with "Speaker X:"
      const speakerMatch = line.match(/^(Speaker \d+):(.*)/);
      if (speakerMatch) {
        const [, speaker, text] = speakerMatch;
        return (
          <div key={index} className="mb-3">
            <span className="font-semibold text-primary">{speaker}:</span>
            <span className="ml-2">
              {searchTerm ? highlightSearchTerm(text, searchTerm) : text}
            </span>
          </div>
        );
      }
      
      // Regular line
      return (
        <div key={index} className="mb-2">
          {searchTerm ? highlightSearchTerm(line, searchTerm) : line}
        </div>
      );
    });
  };

  // Filter files based on search query (including transcript text)
  const filteredFiles = files.filter(file => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      file.name?.toLowerCase().includes(query) ||
      file.groupName?.toLowerCase().includes(query) ||
      file.takeName?.toLowerCase().includes(query) ||
      (Array.isArray(file.talent) && file.talent.some(t => typeof t === 'string' && t.toLowerCase().includes(query))) ||
      file.projectName?.toLowerCase().includes(query) ||
      file.sessionName?.toLowerCase().includes(query) ||
      file.transcript?.toLowerCase().includes(query)
    );
  });

  // Count search matches when search query changes
  useEffect(() => {
    if (searchQuery) {
      const matches = filteredFiles.map(file => {
        const query = searchQuery.toLowerCase();
        let count = 0;
        
        // Count matches in transcript
        if (file.transcript) {
          const transcriptLower = file.transcript.toLowerCase();
          let index = 0;
          while ((index = transcriptLower.indexOf(query, index)) !== -1) {
            count++;
            index += query.length;
          }
        }
        
        // Count matches in other fields
        if (file.name?.toLowerCase().includes(query)) count++;
        if (file.groupName?.toLowerCase().includes(query)) count++;
        if (file.takeName?.toLowerCase().includes(query)) count++;
        if (file.talent?.some(t => t.toLowerCase().includes(query))) count++;
        
        return { fileId: file.id, matchCount: count };
      }).filter(m => m.matchCount > 0);
      
      setSearchMatches(matches);
      setCurrentSearchResult(matches.length > 0 ? 0 : -1);
    } else {
      setSearchMatches([]);
      setCurrentSearchResult(-1);
    }
  }, [searchQuery, filteredFiles]);

  // Navigate search results - simplified to actually work
  const navigateSearchResult = (direction: 'next' | 'prev') => {
    if (filteredFiles.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentSearchResult + 1) % filteredFiles.length;
    } else {
      newIndex = currentSearchResult === 0 ? filteredFiles.length - 1 : currentSearchResult - 1;
    }
    
    setCurrentSearchResult(newIndex);
    
    // Scroll to the file at the new index
    const fileToScroll = filteredFiles[newIndex];
    if (fileToScroll) {
      setTimeout(() => {
        const element = document.getElementById(`file-${fileToScroll.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    }
  };

  // Toggle transcript expansion
  const toggleTranscript = (fileId: string) => {
    setExpandedTranscripts(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Handle select all/deselect all
  const handleSelectAll = () => {
    const filteredIds = filteredFiles.map(f => f.id);
    if (selectedFiles.length === filteredIds.length && filteredIds.length > 0) {
      // Deselect all
      setSelectedFiles([]);
    } else {
      // Select all filtered files
      setSelectedFiles(filteredIds);
    }
  };

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Transcribe</span>
      </div>

      <header
        style={{
          padding: '10px 0 28px',
          borderBottom: '1px solid var(--hj-hairline-soft)',
          marginBottom: 28,
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10 }}>Transcribe</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            lineHeight: 1,
            color: 'var(--hj-ink)',
            letterSpacing: '-0.025em',
          }}
        >
          Words from the take.
        </h1>
        <p
          className="m-0"
          style={{
            marginTop: 12,
            color: 'var(--hj-ink)',
            fontSize: 14.5,
            maxWidth: 580,
            lineHeight: 1.5,
          }}
        >
          Import takes from Casting (or drop in), pick the ones to transcribe, then search every line across the session.
        </p>
      </header>

      <div className="flex flex-col" style={{ gap: 16 }}>
        {/* Project Selector */}
        <div
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <div className="flex items-center gap-3">
            <Select value={currentProject || ''} onValueChange={handleProjectChange}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select a project">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    {currentProject || 'No project selected'}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project} value={project}>
                    {project}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {currentProject && (
              <button
                type="button"
                onClick={() => handleDeleteProject(currentProject)}
                aria-label="Delete project"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-3)',
                  borderRadius: 999,
                  padding: 8,
                  cursor: 'pointer',
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div
          style={{
            padding: 22,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
            <div>
              <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Takes</div>
              <h2
                className="font-hj-display m-0"
                style={{ marginTop: 4, fontSize: 18, color: 'var(--hj-ink)' }}
              >
                {files.length > 0 ? `${files.length} clip${files.length === 1 ? '' : 's'}` : 'No takes yet'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const token = document.cookie.includes('token=') ||
                               document.cookie.includes('castingAuthToken=');
                  if (!token) {
                    setShowLoginModal(true);
                  } else {
                    setShowImporter(true);
                  }
                }}
                className="inline-flex items-center gap-1.5"
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  minHeight: 36,
                }}
              >
                <Import className="w-4 h-4" />
                Import
              </button>
              <button
                type="button"
                onClick={() => {
                  const token = document.cookie.includes('token=') ||
                               document.cookie.includes('castingAuthToken=');
                  if (!token) {
                    setShowLoginModal(true);
                  } else {
                    setShowLibraryPicker(true);
                  }
                }}
                className="inline-flex items-center gap-1.5"
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  minHeight: 36,
                }}
              >
                <FolderOpen className="w-4 h-4" />
                Choose from your library
              </button>
              {selectedFiles.length > 0 && (
                <button
                  type="button"
                  onClick={handleTranscribe}
                  disabled={isTranscribing}
                  className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
                  style={{
                    padding: '8px 16px',
                    borderRadius: 999,
                    background: 'var(--hj-blue)',
                    border: '1px solid var(--hj-blue)',
                    color: 'oklch(0.18 0.005 70)',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isTranscribing ? 'not-allowed' : 'pointer',
                    opacity: isTranscribing ? 0.6 : 1,
                    minHeight: 36,
                  }}
                >
                  {isTranscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                  Transcribe ({selectedFiles.length})
                </button>
              )}
            </div>
          </div>
          <div>
            {/* Search and Select All Bar */}
            {files.length > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-2 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                    <Input
                      type="text"
                      placeholder="Search videos by name, talent, group, or transcript..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Search results navigation */}
                  {searchQuery && filteredFiles.length > 0 && (
                    <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded">
                      <span className="text-sm font-medium">
                        {currentSearchResult + 1} / {filteredFiles.length}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        results
                      </span>
                      <div className="flex gap-0.5 ml-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => navigateSearchResult('prev')}
                        >
                          <ChevronUp className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => navigateSearchResult('next')}
                        >
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {searchQuery && filteredFiles.length === 0 && (
                    <span className="text-sm text-muted-foreground">
                      No matches
                    </span>
                  )}
                </div>
                
                {/* Select All button on the right */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedFiles.length === filteredFiles.map(f => f.id).filter(id => selectedFiles.includes(id)).length && filteredFiles.length > 0 && selectedFiles.length > 0 ? (
                    <>
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <Square className="w-4 h-4 mr-2" />
                      Select All {filteredFiles.length > 0 && `(${filteredFiles.length})`}
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {files.length === 0 ? (
              <div
                {...getRootProps()}
                className="text-center cursor-pointer transition-colors"
                style={{
                  padding: '40px 24px',
                  borderRadius: 'var(--hj-r-md)',
                  border: '1.5px dashed var(--hj-hairline)',
                  background: 'var(--hj-surface-2)',
                }}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'var(--hj-surface-3)',
                      color: 'var(--hj-ink-2)',
                    }}
                  >
                    <Upload className="w-5 h-5" />
                  </div>
                  <p
                    className="font-hj-display m-0"
                    style={{ fontSize: 18, color: 'var(--hj-ink)' }}
                  >
                    Drop videos here or click Import
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--hj-ink-2)' }}>
                    Import takes from Casting to transcribe.
                  </p>
                </div>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div
                className="text-center"
                style={{ padding: '32px 16px', color: 'var(--hj-ink-2)' }}
              >
                <p style={{ fontSize: 14, margin: 0 }}>No videos match "{searchQuery}"</p>
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    marginTop: 8,
                    background: 'transparent',
                    border: 0,
                    color: 'var(--hj-yellow-ink)',
                    fontSize: 13,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredFiles.map(file => {
                  try {
                    return (
                  <div key={file.id} id={`file-${file.id}`} className="space-y-2">
                    <div
                      className="flex items-center gap-3 transition-colors cursor-pointer"
                      style={{
                        padding: 12,
                        borderRadius: 'var(--hj-r-sm)',
                        background: 'var(--hj-surface-2)',
                        border: `1px solid ${
                          searchQuery && filteredFiles[currentSearchResult]?.id === file.id
                            ? 'var(--hj-blue)'
                            : 'var(--hj-hairline)'
                        }`,
                        boxShadow:
                          searchQuery && filteredFiles[currentSearchResult]?.id === file.id
                            ? '0 0 0 2px var(--hj-blue-tint)'
                            : 'none',
                      }}
                      onClick={() => file.transcriptionStatus === 'DONE' && toggleTranscript(file.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFiles([...selectedFiles, file.id]);
                          } else {
                            setSelectedFiles(selectedFiles.filter(id => id !== file.id));
                          }
                        }}
                        className="w-4 h-4"
                        disabled={file.transcriptionStatus === 'TRANSCRIBING' || file.transcriptionStatus === 'DONE'}
                      />
                      
                      {file.thumbnailUrl && (
                        <div className="relative group cursor-pointer" onClick={(e) => {
                          e.stopPropagation();
                          setPlayingVideo(file);
                        }}>
                          <img 
                            src={file.thumbnailUrl} 
                            alt={file.name}
                            className="w-20 h-12 object-cover rounded group-hover:opacity-75 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <Play className="w-6 h-6 text-white drop-shadow-lg" />
                          </div>
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: 'var(--hj-ink)', fontSize: 13.5 }}>
                          {(() => {
                            let displayText = file.takeName || file.name;
                            if (file.takeName) {
                              const parts = file.takeName.split(' - ');
                              if (parts.length >= 2 && parts[parts.length - 1].includes('Take')) {
                                displayText = parts[parts.length - 1];
                              }
                            }
                            return searchQuery
                              ? highlightSearchTerm(displayText, searchQuery)
                              : displayText;
                          })()}
                        </p>
                        <p className="truncate" style={{ fontSize: 12, color: 'var(--hj-ink-3)', margin: 0 }}>
                          {(() => {
                            // Show "Talent Name - Role" format
                            let talentDisplay = '';
                            
                            // Use groupName as the talent name if available
                            if (file.groupName) {
                              talentDisplay = file.groupName;
                              
                              // Extract role from takeName if it has format "ROLE - Take X"
                              if (file.takeName) {
                                const parts = file.takeName.split(' - ');
                                if (parts.length >= 2 && !parts[0].includes('Take')) {
                                  // Add the role
                                  talentDisplay += ` - ${parts[0]}`;
                                }
                              }
                            } else if (file.talent && Array.isArray(file.talent) && file.talent.length > 0) {
                              talentDisplay = file.talent.join(', ');
                            }
                            
                            if (!talentDisplay) return null;
                            
                            return searchQuery ? 
                              highlightSearchTerm(talentDisplay, searchQuery) : 
                              talentDisplay;
                          })()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {file.transcriptionStatus === 'READY_FOR_TRANSCRIPTION' && (
                          <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Ready</span>
                        )}
                        {file.transcriptionStatus === 'TRANSCRIBING' && (
                          <div className="flex items-center gap-1" style={{ color: 'var(--hj-yellow-ink)' }}>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="mono-label">Transcribing</span>
                          </div>
                        )}
                        {file.transcriptionStatus === 'DONE' && (
                          <div className="flex items-center gap-1" style={{ color: 'var(--hj-green)' }}>
                            <CheckCircle className="w-4 h-4" />
                            <span className="mono-label">Done</span>
                            {expandedTranscripts.includes(file.id) ? (
                              <ChevronUp className="w-4 h-4 ml-1" />
                            ) : (
                              <ChevronDown className="w-4 h-4 ml-1" />
                            )}
                          </div>
                        )}
                        {file.transcriptionStatus === 'FAILED' && (
                          <div className="flex items-center gap-1" style={{ color: 'hsl(var(--destructive))' }}>
                            <AlertCircle className="w-4 h-4" />
                            <span className="mono-label">Failed</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Video Player */}
                    {playingVideo?.id === file.id && (
                      <div className="mt-4 mb-4">
                        <div className="relative bg-black rounded-lg overflow-hidden">
                          <video
                            src={resolveVideoUrl(file.s3Url, file.s3Key) || file.s3Url}
                            controls
                            autoPlay
                            className="w-full max-h-[400px]"
                            onEnded={() => setPlayingVideo(null)}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPlayingVideo(null);
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                            title="Close video"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {file.transcriptionStatus === 'DONE' && file.transcript && expandedTranscripts.includes(file.id) && (
                      <div
                        className="ml-7"
                        style={{
                          padding: 14,
                          borderRadius: 'var(--hj-r-sm)',
                          background: 'var(--hj-surface-3)',
                          border: '1px solid var(--hj-hairline-soft)',
                        }}
                      >
                        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                          <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Transcript</div>
                          <button
                            type="button"
                            onClick={() => copyTranscript(file.transcript!)}
                            className="inline-flex items-center gap-1.5"
                            style={{
                              padding: '5px 10px',
                              borderRadius: 999,
                              background: 'transparent',
                              border: '1px solid var(--hj-hairline)',
                              color: 'var(--hj-ink-2)',
                              fontSize: 11.5,
                              cursor: 'pointer',
                            }}
                          >
                            <Copy className="w-3 h-3" />
                            Copy
                          </button>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: 'var(--hj-ink-2)',
                            maxHeight: 192,
                            overflowY: 'auto',
                            lineHeight: 1.5,
                          }}
                        >
                          {formatTranscriptDisplay(file.transcript, searchQuery)}
                        </div>
                      </div>
                    )}
                  </div>
                    );
                  } catch (error) {
                    console.error('Error rendering file:', file, error);
                    return null;
                  }
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />

      <CastingVideoImporter
        open={showImporter}
        onOpenChange={setShowImporter}
        onVideosImported={handleVideosImported}
      />

      <LibraryPicker
        open={showLibraryPicker}
        onClose={() => setShowLibraryPicker(false)}
        onPick={handleLibraryPick}
      />
    </main>
  );
};